import { useHistory, useLocation } from "react-router-dom";
import { client } from "../../hooks/Client"
import { useFirestore, useFirestoreOrderBy} from "../../firebase/useFirestore";
import { Auth } from '../../StateManagment/Auth';
import recordStatusChange from '../../helpers/statusHistory/recordStatusChange';
import usePersonaStatusChange from '../../hooks/personas/usePersonaStatusChange';
import { PERSONA_STATUS_KEYS } from '../../helpers/personas/personaStatusLabels';
import PersonaStatusSelect from '../../components/personas/PersonaStatusSelect';
import useStatusDisplays from '../../hooks/useStatusDisplay';
import buildStatusTabs, { countByStatus } from '../../helpers/statusConfig/buildStatusTabs';
import { useContext, useEffect, useState } from "react";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DummyPhoto from '../../images/dummy-profile-photo.jpeg'
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import { db, timestamp } from "../../firebase/config.js"
import uuid from "react-uuid";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import DeleteModal from "../../components/common/DeleteModal";
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import Modal from '../../components/common/Modal';
import SelectableGrid from "../../components/common/SelectableGrid";
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import TableDragHandleCell from "../../components/common/table/TableDragHandleCell.jsx";
import StarToggle from "../../components/common/StarToggle.jsx";
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { isStarred, filterStarred, starredPatch } from "../../helpers/starred/starred.js";
import TableTitleCell from "../../components/common/table/TableTitleCell.jsx";
import usePersonaTerms from "../../hooks/usePersonaTerms";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import usePermissions from '../../hooks/usePermissions';

// Afgeleid van de canonieke lijst, zodat een nieuwe status meteen filterbaar
// is. De dashboardkaart deeplinkt hierheen met `?status=<id>`; een id dat hier
// niet in staat valt stil terug op 'active'.
const VALID_STATUSES = [...PERSONA_STATUS_KEYS, 'all']

const Personas = () => {
    // Context
    const [saved, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // State
    const [dragArray, setDragArray] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteDocid, setDeleteDocid] = useState('123')
    const [deleteName, setDeleteName] = useState('')
    const readStatusFromSearch = (search) => {
        const params = new URLSearchParams(search)
        const fromUrl = params.get('status')
        return VALID_STATUSES.includes(fromUrl) ? fromUrl : 'active'
    }
    const location = useLocation()
    const [selectedType, setSelectedType] = useState(() => readStatusFromSearch(location.search))
    // Het filter 'Met ster': een weergavekeuze van dit moment.
    // AI-CONTEXT: See docs/features/planner-overzicht-bkb.md
    const [starOnly, setStarOnly] = useState(false)

    /**
     * De ster 'extra aandacht' zetten of weghalen, met wie en wanneer.
     * @param {object} item - De deelnemer
     * @param {boolean} next - De nieuwe stand
     */
    const starHandler = async (item, next) => {
        await db.collection('Personas').doc(item.docid).update(starredPatch(next, auth?.ID, timestamp))
        setSaved('flex')
    }

    useEffect(() => {
        const next = readStatusFromSearch(location.search)
        setSelectedType(prev => (prev === next ? prev : next))
    }, [location.search])

    // Hooks
    const history = useHistory()
    const statusChange = usePersonaStatusChange({ changedBy: auth?.ID, onSaved: () => setSaved('flex') })
    const personaTerms = usePersonaTerms()
    const id = uuid()

    // De knop Opslaan staat in de voet van de gedeelde modal en dus BUITEN
    // de ReadOnlyGuard om de invoervelden. Een uitgeschakelde fieldset dekt
    // hem daar niet meer, dus de poort moet hier apart heen.
    // Bewust NIET `canEdit`: die naam deelt ReadOnlyGuard hieronder al uit
    // als renderparameter, en een tweede zou die overschaduwen.
    // AI-CONTEXT: See docs/pitfalls.md#dev-reused-name-repoints-consumers
    // AI-CONTEXT: See docs/patterns.md#hook-permission-gate
    const { has, ready } = usePermissions()
    const canEditStatus = !ready || has('personas.edit')

    // Firestore
    const personas = useFirestoreOrderBy('Personas', 'Position', 'asc')
    const personaItems = Array.isArray(personas) ? personas : []
    // Eén keer per pagina, niet per rij: useSettings zet eigen listeners op.
    const statusDisplay = useStatusDisplays().persona
    const routes = useFirestore('PersonaRoutes')

     // Delete modal 
     const deleteModalHandler = (e) => {
        const docid = e.currentTarget.dataset.docid
        const deleteName = e.currentTarget.dataset.deletename

        setDeleteDocid(docid)
        setDeleteName(deleteName)
        setDeleteModal(!deleteModal)
    }

    // Volgorde uit de canonieke lijst (het traject dat een deelnemer doorloopt,
    // dezelfde volgorde als de statusgrafiek op het dashboard); labels en
    // zichtbaarheid uit de instelling van de organisatie. De regels erachter
    // — verborgen maar nog bezet, en de tab waar je nu naar kijkt — staan in
    // buildStatusTabs, met tests.
    const options = buildStatusTabs({
        display: statusDisplay,
        counts: countByStatus(personaItems, PERSONA_STATUS_KEYS),
        selectedKey: selectedType,
        total: personaItems.length,
    });

    // Whenever Firestore data changes, update dragArray state
    // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
    useEffect(() => {
        setDragArray(personaItems);
    }, [personas]);

    // DRAG & DROP: reorder items and update Firestore
    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(dragArray);
        const [movedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, movedItem);

        // Update local state to reflect the reorder immediately
        setDragArray(items);

        // Now update Firestore with new positions
        items.forEach((item, idx) => {
        db.collection('Personas')
            .doc(item.docid) // assuming .docid is stored for each doc
            .update({ Position: idx + 1 });
        });

        // Show "saved" icon
        setSaved('flex');
    };

    // Add Persona
    const addPersona = () => {

        db.collection('Personas')
        .doc()
        .set({
            Name: '',
            ID: id,
            Photo: DummyPhoto,
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
            Activities: [],
            Effects: [],
            Research: [],
            Position: personaItems.length + 1,
            Status: 'active',
            OutflowReason: '',
            InactiveReason: '',
            OutflowDate: null,
            InactiveDate: null,
        })
        .then(() => recordStatusChange({
            // Beginregel van de statushistorie: hier begint het traject.
            entityType: 'persona',
            entityID: id,
            toStatus: 'active',
            changedBy: auth?.ID,
        }))
    }

    // Edit Persona
    const nameHandler = (e) => {

        const docid = e.target.dataset.docid
        const value = e.target.value

        db.collection('Personas')
        .doc(docid)
        .update({
            Name: value
        })
        .then(() => {
            setSaved('flex')
         })

    }

    // Status Handler — uitstroom en inactiviteit openen het venster, de rest
    // wordt meteen geschreven. Alleen de velden van díe wissel.
    // AI-CONTEXT: See docs/pitfalls.md#state-handler-overwrites-sibling-field
    const statusHandler = (e) => {
        const directe = statusChange.request({
            docid: e.target.dataset.docid,
            personaId: e.target.dataset.id,
            fromStatus: e.target.dataset.status,
        }, e.target.options[e.target.selectedIndex].value)
        // Een wissel zonder venster heeft geen plek voor de melding.
        if (directe) directe.then((melding) => { if (melding) alert(melding) })
    }

    // Route Handler
    const routeHandler = (e) => {
        const docid = e.target.dataset.docid
        const value = e.target.options[e.target.selectedIndex].value

        if(value === 'create') {
            history.push(`/${client}/settings/6`)
            return
        }

        db.collection('Personas')
        .doc(docid)
        .update({
            RouteID: value
        })
        .then(() => {
            setSaved('flex')
         })
    }

    // Delete Persona
    //
    // Soft delete (ADR-008). Een deelnemer draagt een lange staart aan gegevens —
    // rooster, aanwezigheid, notities, doelen, metingen, beoordelingen — die
    // allemaal op zijn ID staan. Een harde delete liet die staart als wezen achter
    // en was niet terug te draaien; nu verdwijnt de deelnemer uit alle lijsten
    // (de useFirestoreQuery-engine filtert soft deletes centraal) en staat hij in
    // de prullenbak, waar hij terug te zetten is.
    const deletePersona = () => {

        db.collection('Personas')
        .doc(deleteDocid)
        .update({
            Deleted: true,
            DeletedAt: timestamp,
            DeletedBy: auth?.ID || '',
        })
        .then(() => setSaved('flex'))
        .catch((err) => console.error('deelnemer niet verwijderd:', err))
    }

  return (
    <div className="main">
        <div className="main-container" >
        <div className='page-header'>
            <h1>{personaTerms.plural}</h1>
        </div>
        <SelectableGrid
            items={options}
            selectedId={selectedType}
            getId={item => item.id}
            getLabel={item => item.label}
            onClick={setSelectedType}
        />
        {/* Het filter 'Met ster' staat op de regel van het zoekveld, net als
            de filters in het huishoudenoverzicht. */}
        <ReadOnlyGuard edit="personas.edit">
            {(canEdit) => (
                <Table
                    columns={[
                        { key: 'drag', label: '', searchable: false },
                        { key: 'star', label: '', searchable: false },
                        { key: 'title', label: 'TITEL' },
                        {
                            key: 'status',
                            label: 'STATUS',
                            searchable: false,
                            headerContent: (
                                <div className="th-title-container">
                                    <p>STATUS</p>
                                    <UnfoldMoreOutlinedIcon />
                                </div>
                            ),
                        },
                        { key: 'route', label: 'TRAJECT', searchable: false },
                        { key: 'delete', label: 'VERWIJDER', searchable: false },
                    ]}
                    items={(starOnly ? filterStarred(dragArray) : dragArray).filter(p => selectedType === 'all' || p.Status === selectedType)}
                    draggable
                    droppableId="personas"
                    onDragEnd={handleDragEnd}
                    emptyMessage={`Nog geen ${personaTerms.pluralLower} gevonden.`}
                    searchPlaceholder="Zoeken op titel"
                    searchBarStart={(
                        <button
                            type="button"
                            className={`star-filter${starOnly ? ' star-filter--on' : ''}`}
                            aria-pressed={starOnly}
                            aria-label="Met ster"
                            title="Met ster"
                            onClick={() => setStarOnly((aan) => !aan)}
                        >
                            <StarRoundedIcon fontSize="small" />
                        </button>
                    )}
                    searchAccessor={(persona) => persona?.Name || ''}
                    renderRow={(item) => (
                        <>
                            <TableDragHandleCell>
                                <DragIndicatorOutlinedIcon />
                            </TableDragHandleCell>
                            <TableCell>
                                <StarToggle starred={isStarred(item)} disabled={!canEdit} onToggle={(next) => starHandler(item, next)} />
                            </TableCell>
                            <TableTitleCell
                                value={item.Name}
                                to={`/${client}/personabuilder/${item.ID}`}
                                onBlur={nameHandler}
                                docid={item.docid}
                                placeholder='Noteer hier de naam van de persona'
                            />
                            <TableCell>
                                <PersonaStatusSelect
                                    value={item.Status}
                                    display={statusDisplay}
                                    docid={item.docid}
                                    id={item.ID}
                                    onChange={statusHandler}
                                />
                            </TableCell>
                            <TableCell>
                                <select name="" id="" data-docid={item.docid} onChange={routeHandler} value={item.RouteID}>
                                    <option value="">-- Selecteer een traject --</option>
                                    {routes && routes.map(route => (
                                        <option key={route.ID} value={route.ID}>{route.Title}</option>
                                    ))}
                                    <option value="create">-- Creeer nieuw traject --</option>
                                </select>
                            </TableCell>
                            <TableCell>
                                {canEdit && (
                                    <DeleteOutlinedIcon className='table-delete-icon' data-docid={item.docid} data-deletename={item.Name} onClick={deleteModalHandler} />
                                )}
                            </TableCell>
                        </>
                    )}
                    addRow={canEdit ? {
                        toolTipContent: `${personaTerms.singular} toevoegen`,
                        onClick: addPersona,
                    } : undefined}
                />
            )}
        </ReadOnlyGuard>
        <DeleteModal deleteModal={deleteModal} setDeleteModal={setDeleteModal} deleteItem={deletePersona} deleteName={deleteName} softDelete />
    </div>
        <Modal
        modalOpen={statusChange.open}
        setModalOpen={(open) => { if (!open) statusChange.cancel() }}
        pageHeader={statusChange.status === 'outflow' ? 'Uitstroom' : 'Inactiviteit'}
        confirm={statusChange.confirm}
        confirmDisabled={!canEditStatus || statusChange.saving}
        content={
            <ReadOnlyGuard edit="personas.edit">
                <h3>Reden {statusChange.status === 'outflow' ? 'uitstroom' : 'inactiviteit'}</h3>
                <p>Geef een reden op voor de {statusChange.status === 'outflow' ? 'uitstroom' : 'inactiviteit'}.</p>
                <textarea
                    placeholder={statusChange.status === 'outflow' ? 'Reden voor uitstroom...' : 'Reden voor inactiviteit...'}
                    value={statusChange.reason}
                    onChange={(e) => statusChange.setReason(e.target.value)}
                ></textarea>
                <h3>Datum van {statusChange.status === 'outflow' ? 'uitstroom' : 'inactiviteit'}</h3>
                <input type="date" value={statusChange.date} onChange={(e) => statusChange.setDate(e.target.value)}/>
                {statusChange.error && <p className="persona-status-error">{statusChange.error}</p>}
            </ReadOnlyGuard>
        }
        />
</div>
  )
}

export default Personas
